import { Paper, Text, Group, Stack } from '@mantine/core';
import { DisplayStatus } from '../../types/status';
import { StatusBadge } from './StatusBadge';
import { ActionButton } from './ActionButton';
import { IconCalendarDue } from '@tabler/icons-react';
import dayjs from 'dayjs';

interface ReportStatusCardProps {
  id: string;
  title: string;
  dueDate: string;
  status: DisplayStatus;
  isInProgress?: boolean;
  /** When true, only a View action is shown (e.g. admin reports list). */
  viewOnly?: boolean;
  onSubmit: (reportId: string) => void;
  onView: (reportId: string) => void;
  onUpdate: (reportId: string) => void;
}

const getActionButton = (
  displayStatus: DisplayStatus,
  reportId: string,
  isInProgress: boolean = false,
  { onSubmit, onView, onUpdate }: Pick<ReportStatusCardProps, 'onSubmit' | 'onView' | 'onUpdate'>,
  viewOnly?: boolean
): JSX.Element => {
  if (viewOnly) {
    return (
      <ActionButton
        onClick={() => onView(reportId)}
        label="View"
        ariaLabel={`View report ${reportId}`}
        actionType="primary"
        width={100}
      />
    );
  }

  // New logic for not_submitted and past_due based on isInProgress
  if (displayStatus === 'not_submitted' || displayStatus === 'past_due') {
    if (!isInProgress) {
      return (
        <ActionButton
          onClick={() => onSubmit(reportId)}
          label="Start"
          ariaLabel={`Start report ${reportId}`}
          actionType="primary"
          width={100}
        />
      );
    } else {
      return (
        <ActionButton
          onClick={() => onSubmit(reportId)}
          label="Continue"
          ariaLabel={`Continue report ${reportId}`}
          actionType="primary"
          width={100}
        />
      );
    }
  }

  switch (displayStatus) {
    case 'needs_attention':
      return (
        <ActionButton
          onClick={() => onUpdate(reportId)}
          label="Update"
          ariaLabel={`Update report ${reportId}`}
          actionType="primary"
          width={100}
        />
      );
    case 'in_review':
    case 'approved':
    default:
      return (
        <ActionButton
          onClick={() => onView(reportId)}
          label="View"
          ariaLabel={`View report ${reportId}`}
          actionType="primary"
          width={100}
        />
      );
  }
};

export function ReportStatusCard({ 
  id,
  title,
  dueDate,
  status,
  isInProgress = false,
  viewOnly = false,
  onSubmit,
  onView,
  onUpdate 
}: ReportStatusCardProps) {
  const isPastDue = dayjs(dueDate).isBefore(dayjs(), 'day') && status === 'not_submitted';
  const displayStatus: DisplayStatus = isPastDue ? 'past_due' : status;

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <Paper 
      tabIndex={0}
      onKeyDown={(e) => handleKeyDown(e, () => onView(id))}
      shadow="md" 
      p="md" 
      radius="md"
      role="article"
      aria-label={title}
      sx={(theme) => ({
        height: '180px', 
        display: 'flex', 
        flexDirection: 'column',
        border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
        boxShadow: theme.colorScheme === 'dark' 
          ? '0 0 12px rgba(255, 255, 255, 0.1)' 
          : theme.shadows.md,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.colorScheme === 'dark'
            ? '0 0 16px rgba(255, 255, 255, 0.15)'
            : '0 8px 16px rgba(0, 0, 0, 0.1)'
        },
        '&:focus': {
          outline: 'none',
          borderColor: theme.colors.blue[5]
        },
        '@media (max-width: 768px)': {
          height: 'auto',
          minHeight: '160px',
          padding: theme.spacing.sm
        }
      })}
    >
      <Stack spacing={4} mb="md">
        <Text weight={500} size="lg" sx={(theme) => ({
          '@media (max-width: 768px)': {
            fontSize: theme.fontSizes.md,
            lineHeight: 1.3
          }
        })}>{title}</Text>
        <Group spacing="xs" noWrap>
          <IconCalendarDue 
            size={16}
            style={{ color: 'var(--mantine-color-dimmed)' }}
            aria-label="Due date icon"
          />
          <Text 
            size="sm" 
            weight={500}
            aria-label={`Due date: ${dayjs(dueDate).format('MMM D, YYYY')}`}
            sx={(theme) => ({
              '@media (max-width: 768px)': {
                fontSize: theme.fontSizes.xs
              }
            })}
          >
            {dayjs(dueDate).format('MMM D, YYYY')}
          </Text>
        </Group>
      </Stack>

      <Group mt="auto" position="apart" sx={(theme) => ({
        '@media (max-width: 480px)': {
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: theme.spacing.sm
        }
      })}>
        <StatusBadge status={displayStatus} />
        {getActionButton(displayStatus, id, isInProgress, { 
          onSubmit, 
          onView,
          onUpdate 
        }, viewOnly)}
      </Group>
    </Paper>
  );
} 